import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  apiGetArticlesRedaction,
  apiGetArticle,
  apiCreateArticle,
  apiUpdateArticle,
  apiDeleteArticle,
} from '../services/api';
import ArticleView from '../components/ArticleView';
import { storeBlogDraft, readBlogDraft, clearBlogDraft } from '../utils/blogDraft';
import { CATEGORIES, formatDate } from './articles.utils';
import './Blogs.css';

// Vocabulaire aligné sur `cycle_engine.Phase` (schemas/article.py: PHASES).
const PHASE_OPTIONS = [
  { value: 'REGLES', label: 'Règles' },
  { value: 'FOLLICULAIRE', label: 'Folliculaire' },
  { value: 'OVULATION', label: 'Ovulation' },
  { value: 'LUTEALE', label: 'Lutéale' },
];

// Vocabulaire des blocs de corps (schemas/article.py: Bloc). L'ordre de cette liste
// est celui de la barre d'ajout, pas une contrainte de rédaction : un article se
// compose de ces blocs dans n'importe quel ordre.
const BLOC_TYPES = [
  { value: 'titre', label: 'Titre' },
  { value: 'paragraphe', label: 'Paragraphe' },
  { value: 'image', label: 'Image' },
  { value: 'puces', label: 'Puces' },
  { value: 'liste', label: 'Liste numérotée' },
];

const BLOC_LABELS = Object.fromEntries(BLOC_TYPES.map((t) => [t.value, t.label]));

const RICH_TEXT_HINT = 'Mise en forme : **gras** et [libellé](https://…)';

// Délai d'inactivité avant écriture du brouillon local. Assez court pour qu'une
// fermeture d'onglet ne coûte qu'une phrase, assez long pour ne pas sérialiser
// l'article à chaque touche.
const AUTOSAVE_DELAI_MS = 1200;

const aujourdHuiISO = () => new Date().toISOString().slice(0, 10);

// Les trois états de la liste. « À venir » n'existe pas côté serveur : c'est un article
// publié dont la date n'est pas encore arrivée, et il basculera de lui-même dans
// « Publiés » le jour dit — d'où un calcul ici plutôt qu'un champ de plus.
export const etatArticle = (a) => {
  if (a.statut === 'brouillon') return 'brouillons';
  return a.date_publication > aujourdHuiISO() ? 'avenir' : 'publies';
};

const ONGLETS = [
  { id: 'publies', label: 'Publiés', vide: 'Aucun article en ligne pour le moment.' },
  { id: 'avenir', label: 'À venir', vide: 'Aucune publication programmée. Donne une date future à un article pour le préparer à l’avance.' },
  { id: 'brouillons', label: 'Brouillons', vide: 'Aucun brouillon en cours.' },
];

const formatHeure = (ts) =>
  new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

const ilYA = (ts) => {
  const minutes = Math.round((Date.now() - ts) / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const heures = Math.round(minutes / 60);
  if (heures < 24) return `il y a ${heures} h`;
  return `le ${new Date(ts).toLocaleDateString('fr-FR')}`;
};

// Un bloc de formulaire porte tous les champs possibles, quel que soit son type :
// `buildPayload` ne retient que ceux qui comptent. Ça évite un état trouant à
// chaque changement de type et garde les handlers uniformes.
const emptyBloc = (type) => ({
  type,
  texte: '',
  url: '',
  legende: '',
  items:
    type === 'puces' ? [''] :
    type === 'liste' ? [{ sous_titre: '', texte: '' }] :
    [],
});

const toFormBloc = (b) => ({
  ...emptyBloc(b.type),
  texte: b.texte || '',
  url: b.url || '',
  legende: b.legende || '',
  items:
    b.type === 'puces' ? (b.items || []).map((it) => String(it)) :
    b.type === 'liste' ? (b.items || []).map((it) => ({ sous_titre: it.sous_titre || '', texte: it.texte || '' })) :
    [],
});

const blocToPayload = (b) => {
  switch (b.type) {
    case 'image':
      return { type: 'image', url: b.url.trim(), legende: b.legende.trim() || null };
    case 'puces':
      return { type: 'puces', items: b.items.map((it) => it.trim()).filter(Boolean) };
    case 'liste':
      return {
        type: 'liste',
        items: b.items.map((it) => ({ sous_titre: it.sous_titre.trim(), texte: it.texte.trim() })),
      };
    default:
      return { type: b.type, texte: (b.texte || '').trim() };
  }
};

const emptyForm = () => ({
  titre: '',
  image_url: '',
  categories: [],
  phases: [],
  auteur: '',
  date_publication: new Date().toISOString().slice(0, 10),
  temps_lecture: 5,
  resume: '',
  intro: '',
  callout: '',
  blocs: [],
  a_la_une: false,
});

const buildPayload = (form, statut = 'publie') => ({
  titre: form.titre.trim(),
  image_url: form.image_url.trim() || null,
  categories: form.categories,
  phases: form.phases,
  auteur: form.auteur.trim() || null,
  date_publication: form.date_publication,
  temps_lecture: Number(form.temps_lecture),
  resume: form.a_la_une ? (form.resume.trim() || null) : null,
  intro: form.intro.trim(),
  callout: form.callout.trim() || null,
  a_la_une: form.a_la_une,
  blocs: form.blocs.map(blocToPayload),
  statut,
});

// Double du contrôle backend (`schemas/article.py: _exiger_un_article_complet`), tenu
// ici pour dire ce qui manque dans la langue du formulaire plutôt que de laisser
// remonter un 422. C'est le serveur qui fait foi ; ceci n'est qu'une politesse.
export const manquesPourPublier = (form) => {
  const manques = [];
  if (!form.titre.trim()) manques.push('le titre');
  if (!form.intro.trim()) manques.push("l'introduction");
  form.blocs.forEach((b, i) => {
    const rang = `le bloc ${i + 1} (${BLOC_LABELS[b.type] || b.type})`;
    if ((b.type === 'titre' || b.type === 'paragraphe') && !b.texte.trim()) manques.push(rang);
    if (b.type === 'image' && !b.url.trim()) manques.push(rang);
    if (b.type === 'puces' && !b.items.some((it) => it.trim())) manques.push(rang);
    if (b.type === 'liste' && !b.items.some((it) => it.sous_titre.trim() || it.texte.trim())) {
      manques.push(rang);
    }
  });
  return manques;
};

const Blogs = () => {
  const navigate = useNavigate();
  const { user, getToken, logoutUser } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [mode, setMode] = useState('list');   // 'list' | 'form'
  const [listTab, setListTab] = useState('publies');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [statutEdite, setStatutEdite] = useState('publie');  // statut de l'article ouvert
  const [dirty, setDirty] = useState(false);
  const [autoSavedAt, setAutoSavedAt] = useState(null);
  const [draftPropose, setDraftPropose] = useState(null);    // brouillon local en attente de reprise
  const errorRef = useRef(null);
  // Compteur et non booléen : reclic sur « Publier » avec la *même* erreur, React ne
  // re-rend pas (état identique) et un effet qui n'observerait que `error` ne se
  // redéclencherait pas — le second clic resterait sans effet visible, exactement le
  // symptôme qu'on corrige.
  const [errorTick, setErrorTick] = useState(0);

  const signalerErreur = useCallback((msg) => {
    setError(msg);
    setErrorTick((t) => t + 1);
  }, []);

  const loadArticles = useCallback(() => {
    setLoading(true);
    setError('');
    apiGetArticlesRedaction(getToken())
      .then((data) => setArticles(data || []))
      .catch((err) => signalerErreur(err.message))
      .finally(() => setLoading(false));
  }, [getToken, signalerErreur]);

  useEffect(() => { loadArticles(); }, [loadArticles]);

  // Le message d'erreur vit en tête de page, les boutons d'enregistrement tout en bas
  // d'un formulaire long : sans ce recentrage, un « Publier » refusé ne produisait
  // aucun effet visible et donnait l'impression que le clic n'avait rien déclenché.
  useEffect(() => {
    const cible = errorRef.current;
    // `scrollIntoView` manque sous jsdom et sur quelques navigateurs anciens : mieux
    // vaut ne pas recentrer que faire tomber l'éditeur sur un message d'erreur.
    if (!errorTick || typeof cible?.scrollIntoView !== 'function') return;
    const brutal = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    cible.scrollIntoView({ behavior: brutal ? 'auto' : 'smooth', block: 'center' });
  }, [errorTick]);

  const parOnglet = useMemo(() => {
    const groupes = { publies: [], avenir: [], brouillons: [] };
    articles.forEach((a) => groupes[etatArticle(a)].push(a));
    return groupes;
  }, [articles]);

  // Autosave : débouncé, et seulement une fois qu'une modification réelle a eu lieu.
  useEffect(() => {
    if (mode !== 'form' || !dirty) return undefined;
    const t = setTimeout(() => {
      if (storeBlogDraft(editingId, form)) setAutoSavedAt(Date.now());
    }, AUTOSAVE_DELAI_MS);
    return () => clearTimeout(t);
  }, [form, mode, dirty, editingId]);

  // Dernier rempart pour l'onglet fermé dans la seconde qui suit une frappe, avant que
  // le débounce n'ait écrit.
  useEffect(() => {
    if (mode !== 'form' || !dirty) return undefined;
    const avantFermeture = (e) => {
      storeBlogDraft(editingId, form);
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', avantFermeture);
    return () => window.removeEventListener('beforeunload', avantFermeture);
  }, [form, mode, dirty, editingId]);

  const handleLogout = async () => {
    await logoutUser();
    navigate('/connexion');
  };

  // Un compte `editor` n'a pas d'app sportive où retourner ; tout autre compte à qui la
  // rédaction a été ouverte, si. Sans cette porte, une sportive rédactrice entrée ici ne
  // pouvait en sortir qu'en se déconnectant.
  const canReturnToApp = user?.role !== 'editor';

  // Ouverture commune aux deux entrées : on installe la version de référence, puis on
  // signale (sans l'appliquer) le brouillon local éventuel. Restaurer en silence serait
  // trompeur — l'article a pu être modifié ailleurs depuis.
  const ouvrirFormulaire = (id, valeurs, statut) => {
    setEditingId(id);
    setForm(valeurs);
    setStatutEdite(statut);
    setDirty(false);
    setAutoSavedAt(null);
    setDraftPropose(readBlogDraft(id));
    setMessage('');
    setError('');
    setPreview(false);
    setMode('form');
  };

  const startCreate = () => ouvrirFormulaire(null, emptyForm(), 'publie');

  const startEdit = async (id) => {
    setMessage('');
    setError('');
    try {
      const article = await apiGetArticle(id, getToken());
      ouvrirFormulaire(id, {
        titre: article.titre || '',
        image_url: article.image_url || '',
        categories: article.categories || [],
        phases: article.phases || [],
        auteur: article.auteur || '',
        date_publication: article.date_publication || aujourdHuiISO(),
        temps_lecture: article.temps_lecture || 5,
        resume: article.resume || '',
        intro: article.intro || '',
        callout: article.callout || '',
        blocs: (article.blocs || []).map(toFormBloc),
        a_la_une: !!article.a_la_une,
      }, article.statut || 'publie');
    } catch (err) {
      signalerErreur(err.message);
    }
  };

  const reprendreDraft = () => {
    setForm(draftPropose.form);
    setDirty(true);
    setAutoSavedAt(draftPropose.savedAt);
    setDraftPropose(null);
  };

  const ignorerDraft = () => {
    clearBlogDraft(editingId);
    setDraftPropose(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement cet article ?')) return;
    try {
      await apiDeleteArticle(id, getToken());
      // L'article n'existe plus : son brouillon local n'a plus rien à protéger et
      // resterait indéfiniment dans le stockage du navigateur.
      clearBlogDraft(id);
      setMessage('Article supprimé.');
      loadArticles();
    } catch (err) {
      signalerErreur(err.message);
    }
  };

  const handleSubmit = async (statut) => {
    setError('');
    setMessage('');

    if (statut === 'publie') {
      const manques = manquesPourPublier(form);
      if (manques.length) {
        signalerErreur(
          `Impossible de publier : ${manques.join(', ')} ${manques.length > 1 ? 'sont vides' : 'est vide'}. `
          + 'Complète-les, ou enregistre en brouillon pour y revenir plus tard.'
        );
        return;
      }
    }

    setSaving(true);
    try {
      const payload = buildPayload(form, statut);
      if (editingId) {
        await apiUpdateArticle(editingId, payload, getToken());
        setMessage(statut === 'brouillon' ? 'Brouillon enregistré.' : 'Article publié.');
      } else {
        await apiCreateArticle(payload, getToken());
        setMessage(statut === 'brouillon' ? 'Brouillon créé.' : 'Article publié.');
      }
      // Le filet local n'a plus lieu d'être : la version de référence est en base.
      clearBlogDraft(editingId);
      setDirty(false);
      setListTab(statut === 'brouillon'
        ? 'brouillons'
        : (form.date_publication > aujourdHuiISO() ? 'avenir' : 'publies'));
      setMode('list');
      loadArticles();
    } catch (err) {
      signalerErreur(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (dirty && !window.confirm(
      'Abandonner les modifications non enregistrées ? Le brouillon local sera supprimé.'
    )) return;
    clearBlogDraft(editingId);
    setDirty(false);
    setMode('list');
  };

  // ── Champs de formulaire ────────────────────────────────────────────────
  // Toute mutation passe par `setForm` : marquer « sale » ici couvre l'ensemble des
  // champs et des blocs sans avoir à y penser dans chaque handler.
  const majForm = (updater) => {
    setDirty(true);
    setForm(updater);
  };

  const setField = (field, value) => majForm((f) => ({ ...f, [field]: value }));

  const toggleCategory = (cat) => majForm((f) => ({
    ...f,
    categories: f.categories.includes(cat)
      ? f.categories.filter((c) => c !== cat)
      : [...f.categories, cat],
  }));

  const togglePhase = (phase) => majForm((f) => ({
    ...f,
    phases: f.phases.includes(phase)
      ? f.phases.filter((p) => p !== phase)
      : [...f.phases, phase],
  }));

  const addBloc = (type) => majForm((f) => ({ ...f, blocs: [...f.blocs, emptyBloc(type)] }));

  const removeBloc = (bi) => majForm((f) => ({
    ...f, blocs: f.blocs.filter((_, i) => i !== bi),
  }));

  const updateBloc = (bi, patch) => majForm((f) => ({
    ...f,
    blocs: f.blocs.map((b, i) => (i === bi ? { ...b, ...patch } : b)),
  }));

  // L'ordre des blocs *est* la mise en page : le réordonnancement n'est pas un
  // confort, c'est le seul moyen d'intercaler une image entre deux paragraphes
  // écrits avant elle.
  const moveBloc = (bi, delta) => majForm((f) => {
    const cible = bi + delta;
    if (cible < 0 || cible >= f.blocs.length) return f;
    const blocs = [...f.blocs];
    [blocs[bi], blocs[cible]] = [blocs[cible], blocs[bi]];
    return { ...f, blocs };
  });

  const updateItems = (bi, updater) => majForm((f) => ({
    ...f,
    blocs: f.blocs.map((b, i) => (i === bi ? { ...b, items: updater(b.items || []) } : b)),
  }));

  const addItem = (bi, type) => updateItems(
    bi, (items) => [...items, type === 'puces' ? '' : { sous_titre: '', texte: '' }]
  );

  const removeItem = (bi, ii) => updateItems(bi, (items) => items.filter((_, k) => k !== ii));

  const updatePuce = (bi, ii, value) => updateItems(
    bi, (items) => items.map((it, k) => (k === ii ? value : it))
  );

  const updateItem = (bi, ii, field, value) => updateItems(
    bi, (items) => items.map((it, k) => (k === ii ? { ...it, [field]: value } : it))
  );

  // ── Rendu ────────────────────────────────────────────────────────────────
  return (
    <div className="blogs-page">
      <div className="blogs-page__inner">
        <div className="blogs-page__header">
          <h1 className="blogs-page__title">Espace rédaction</h1>
          <div className="blogs-page__header-actions">
            {canReturnToApp && (
              <button className="blogs-page__back" onClick={() => navigate('/blog')}>
                ← Retour à l'app
              </button>
            )}
            <button className="blogs-page__logout" onClick={handleLogout}>Déconnexion</button>
          </div>
        </div>

        {error && <p className="blogs-page__error" ref={errorRef} role="alert">{error}</p>}
        {message && <p className="blogs-page__notice">{message}</p>}

        {mode === 'list' ? (
          <>
            <div className="blogs-toolbar">
              <div className="blogs-toggle" role="tablist">
                {ONGLETS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={listTab === t.id}
                    className={`blogs-toggle__btn${listTab === t.id ? ' blogs-toggle__btn--active' : ''}`}
                    onClick={() => setListTab(t.id)}
                  >
                    {t.label} <span className="blogs-toggle__count">{parOnglet[t.id].length}</span>
                  </button>
                ))}
              </div>
              <button className="blogs-btn blogs-btn--primary" onClick={startCreate}>+ Nouvel article</button>
            </div>

            {loading ? (
              <p className="blogs-page__notice">Chargement…</p>
            ) : articles.length === 0 ? (
              <p className="blogs-page__notice">Aucun article. Crée le premier avec « Nouvel article ».</p>
            ) : parOnglet[listTab].length === 0 ? (
              <p className="blogs-page__notice">
                {ONGLETS.find((t) => t.id === listTab).vide}
              </p>
            ) : (
              <ul className="blogs-list">
                {parOnglet[listTab].map((a) => (
                  <li key={a.id} className="blogs-list__row">
                    <div className="blogs-list__main">
                      <span className="blogs-list__title">
                        {a.titre || <em>Sans titre</em>}
                        {listTab === 'brouillons' && (
                          <span className="blogs-badge blogs-badge--brouillon">Brouillon</span>
                        )}
                        {listTab === 'avenir' && (
                          <span className="blogs-badge blogs-badge--avenir">À venir</span>
                        )}
                      </span>
                      <span className="blogs-list__meta">
                        {(a.categories || []).join(', ') || '—'}
                        {' · '}
                        {listTab === 'avenir'
                          ? `publication le ${formatDate(a.date_publication)}`
                          : formatDate(a.date_publication)}
                        {' · '}{a.temps_lecture} min
                        {a.a_la_une ? ' · ⭐ à la une' : ''}
                      </span>
                    </div>
                    <div className="blogs-list__actions">
                      <button className="blogs-btn" onClick={() => startEdit(a.id)}>Modifier</button>
                      <button className="blogs-btn blogs-btn--danger" onClick={() => handleDelete(a.id)}>Supprimer</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <form className="blogs-form" onSubmit={(e) => e.preventDefault()}>
            {draftPropose && (
              <div className="blogs-draft-banner">
                <span className="blogs-draft-banner__text">
                  Une version non enregistrée de cet article a été retrouvée sur ce navigateur
                  ({ilYA(draftPropose.savedAt)}).
                </span>
                <div className="blogs-draft-banner__actions">
                  <button type="button" className="blogs-btn blogs-btn--primary" onClick={reprendreDraft}>
                    Reprendre
                  </button>
                  <button type="button" className="blogs-btn" onClick={ignorerDraft}>
                    Repartir de la version enregistrée
                  </button>
                </div>
              </div>
            )}

            <div className="blogs-form__head">
              <h2 className="blogs-form__heading">
                {editingId ? 'Modifier l’article' : 'Nouvel article'}
                {statutEdite === 'brouillon' && (
                  <span className="blogs-badge blogs-badge--brouillon">Brouillon</span>
                )}
              </h2>
              <div className="blogs-toggle" role="tablist">
                {[
                  { id: false, label: 'Rédaction' },
                  { id: true, label: 'Aperçu' },
                ].map((t) => (
                  <button
                    key={String(t.id)}
                    type="button"
                    role="tab"
                    aria-selected={preview === t.id}
                    className={`blogs-toggle__btn${preview === t.id ? ' blogs-toggle__btn--active' : ''}`}
                    onClick={() => setPreview(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {preview ? (
              /* `buildPayload` est exactement ce qui part à l'API : l'aperçu montre
                 donc ce qui sera enregistré, pas une approximation. `id` n'existe pas
                 encore sur un brouillon, mais ArticleCover s'en sert pour choisir son
                 dégradé de repli — d'où la valeur de remplacement. */
              <div className="blogs-preview">
                <div className="blogs-preview__frame">
                  <div className="article-page">
                    <ArticleView article={{ ...buildPayload(form), id: editingId ?? 0 }} />
                  </div>
                </div>
                <p className="blogs-preview__hint">
                  Rendu tel que les lectrices le verront. Rien n'est enregistré tant que
                  tu n'as pas cliqué sur «&nbsp;Enregistrer&nbsp;».
                </p>
              </div>
            ) : (
            <>
            <label className="blogs-field">
              <span className="blogs-field__label">Titre</span>
              <input className="blogs-input" value={form.titre} onChange={(e) => setField('titre', e.target.value)} required />
            </label>

            <label className="blogs-field">
              <span className="blogs-field__label">Image de couverture (optionnel)</span>
              <input
                className="blogs-input"
                type="url"
                inputMode="url"
                placeholder="https://… ou /img/mon-visuel.jpg"
                maxLength={500}
                value={form.image_url}
                onChange={(e) => setField('image_url', e.target.value)}
              />
              <span className="blogs-field__hint">
                Format paysage conseillé (≈16/9, min. 800px de large). Sans image, un
                dégradé et le pictogramme de la catégorie sont affichés.
              </span>
              {form.image_url.trim() && (
                <img
                  className="blogs-image-preview"
                  src={form.image_url.trim()}
                  alt="Aperçu de la couverture"
                />
              )}
            </label>

            <div className="blogs-field">
              <span className="blogs-field__label">Catégories</span>
              <div className="blogs-chips">
                {CATEGORIES.map((cat) => (
                  <button
                    type="button"
                    key={cat}
                    className={`blogs-chip${form.categories.includes(cat) ? ' blogs-chip--active' : ''}`}
                    onClick={() => toggleCategory(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Le blog met en avant ce qui parle de la phase où la lectrice se trouve.
                Ne rien cocher, c'est dire « cet article vaut à tout moment du cycle » —
                il reste visible, simplement pas remonté. */}
            <div className="blogs-field">
              <span className="blogs-field__label">
                Phases concernées
                <span className="blogs-field__hint"> — rien de coché : vaut tout le cycle</span>
              </span>
              <div className="blogs-chips">
                {PHASE_OPTIONS.map(({ value, label }) => (
                  <button
                    type="button"
                    key={value}
                    className={`blogs-chip${form.phases.includes(value) ? ' blogs-chip--active' : ''}`}
                    onClick={() => togglePhase(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <label className="blogs-field">
              <span className="blogs-field__label">Signature (optionnel)</span>
              <input
                className="blogs-input"
                placeholder="Gaëlle Etienne"
                maxLength={120}
                value={form.auteur}
                onChange={(e) => setField('auteur', e.target.value)}
              />
              <span className="blogs-field__hint">
                Affichée sous le titre («&nbsp;Par…&nbsp;»). Laisse vide pour un article non signé.
              </span>
            </label>

            <div className="blogs-field-row">
              <label className="blogs-field">
                <span className="blogs-field__label">Date de publication</span>
                <input type="date" className="blogs-input" value={form.date_publication} onChange={(e) => setField('date_publication', e.target.value)} required />
              </label>
              <label className="blogs-field">
                <span className="blogs-field__label">Temps de lecture (min)</span>
                <input type="number" min="1" max="120" className="blogs-input" value={form.temps_lecture} onChange={(e) => setField('temps_lecture', e.target.value)} required />
              </label>
            </div>

            <label className="blogs-checkbox">
              <input type="checkbox" checked={form.a_la_une} onChange={(e) => setField('a_la_une', e.target.checked)} />
              <span>Mettre à la une (carrousel « Articles du moment »)</span>
            </label>

            {form.a_la_une && (
              <label className="blogs-field">
                <span className="blogs-field__label">Résumé court (affiché sur la carte à la une)</span>
                <textarea className="blogs-input" rows={2} maxLength={300} value={form.resume} onChange={(e) => setField('resume', e.target.value)} />
              </label>
            )}

            <label className="blogs-field">
              <span className="blogs-field__label">Introduction</span>
              <textarea className="blogs-input" rows={4} value={form.intro} onChange={(e) => setField('intro', e.target.value)} required />
            </label>

            <label className="blogs-field">
              <span className="blogs-field__label">Encadré « À savoir » (optionnel)</span>
              <textarea className="blogs-input" rows={2} value={form.callout} onChange={(e) => setField('callout', e.target.value)} />
            </label>

            {/* Constructeur de corps : une pile de blocs, dans l'ordre de lecture */}
            <div className="blogs-field__label blogs-sections__label">
              Corps de l'article
              <span className="blogs-field__hint"> — les blocs s'affichent dans cet ordre</span>
            </div>

            {form.blocs.length === 0 && (
              <p className="blogs-page__notice">
                Aucun bloc. Compose l'article en empilant titres, paragraphes, images et listes.
              </p>
            )}

            {form.blocs.map((bloc, bi) => (
              <div key={bi} className="blogs-section">
                <div className="blogs-section__head">
                  <span className="blogs-bloc__type">{BLOC_LABELS[bloc.type] || bloc.type}</span>
                  <div className="blogs-bloc__move">
                    <button
                      type="button"
                      className="blogs-btn"
                      aria-label="Monter le bloc"
                      disabled={bi === 0}
                      onClick={() => moveBloc(bi, -1)}
                    >↑</button>
                    <button
                      type="button"
                      className="blogs-btn"
                      aria-label="Descendre le bloc"
                      disabled={bi === form.blocs.length - 1}
                      onClick={() => moveBloc(bi, 1)}
                    >↓</button>
                  </div>
                  <button type="button" className="blogs-btn blogs-btn--danger" aria-label="Supprimer le bloc" onClick={() => removeBloc(bi)}>✕</button>
                </div>

                {bloc.type === 'titre' && (
                  <input
                    className="blogs-input"
                    placeholder="Intertitre"
                    value={bloc.texte}
                    onChange={(e) => updateBloc(bi, { texte: e.target.value })}
                  />
                )}

                {bloc.type === 'paragraphe' && (
                  <>
                    <textarea
                      className="blogs-input"
                      rows={4}
                      placeholder="Contenu du paragraphe"
                      value={bloc.texte}
                      onChange={(e) => updateBloc(bi, { texte: e.target.value })}
                    />
                    <span className="blogs-field__hint">{RICH_TEXT_HINT}</span>
                  </>
                )}

                {bloc.type === 'image' && (
                  <>
                    <input
                      className="blogs-input"
                      type="url"
                      inputMode="url"
                      placeholder="https://… ou /img/mon-visuel.jpg"
                      maxLength={500}
                      value={bloc.url}
                      onChange={(e) => updateBloc(bi, { url: e.target.value })}
                    />
                    <input
                      className="blogs-input"
                      placeholder="Légende (optionnel)"
                      maxLength={300}
                      value={bloc.legende}
                      onChange={(e) => updateBloc(bi, { legende: e.target.value })}
                    />
                    {bloc.url.trim() && (
                      <img className="blogs-image-preview" src={bloc.url.trim()} alt="Aperçu du visuel" />
                    )}
                  </>
                )}

                {bloc.type === 'puces' && (
                  <div className="blogs-items">
                    {bloc.items.map((item, ii) => (
                      <div key={ii} className="blogs-item">
                        <span className="blogs-item__num">•</span>
                        <div className="blogs-item__fields">
                          <textarea
                            className="blogs-input"
                            rows={2}
                            placeholder="Ligne de la liste"
                            value={item}
                            onChange={(e) => updatePuce(bi, ii, e.target.value)}
                          />
                        </div>
                        <button type="button" className="blogs-btn blogs-btn--danger" aria-label="Supprimer la ligne" onClick={() => removeItem(bi, ii)}>✕</button>
                      </div>
                    ))}
                    <span className="blogs-field__hint">{RICH_TEXT_HINT}</span>
                    <button type="button" className="blogs-btn" onClick={() => addItem(bi, 'puces')}>+ Ajouter une ligne</button>
                  </div>
                )}

                {bloc.type === 'liste' && (
                  <div className="blogs-items">
                    {bloc.items.map((item, ii) => (
                      <div key={ii} className="blogs-item">
                        <span className="blogs-item__num">{ii + 1}.</span>
                        <div className="blogs-item__fields">
                          <input
                            className="blogs-input"
                            placeholder="Sous-titre"
                            value={item.sous_titre}
                            onChange={(e) => updateItem(bi, ii, 'sous_titre', e.target.value)}
                          />
                          <textarea
                            className="blogs-input"
                            rows={2}
                            placeholder="Texte de l'élément"
                            value={item.texte}
                            onChange={(e) => updateItem(bi, ii, 'texte', e.target.value)}
                          />
                        </div>
                        <button type="button" className="blogs-btn blogs-btn--danger" aria-label="Supprimer l'élément" onClick={() => removeItem(bi, ii)}>✕</button>
                      </div>
                    ))}
                    <span className="blogs-field__hint">{RICH_TEXT_HINT}</span>
                    <button type="button" className="blogs-btn" onClick={() => addItem(bi, 'liste')}>+ Ajouter un élément</button>
                  </div>
                )}
              </div>
            ))}

            <div className="blogs-sections__add">
              {BLOC_TYPES.map(({ value, label }) => (
                <button key={value} type="button" className="blogs-btn" onClick={() => addBloc(value)}>
                  + {label}
                </button>
              ))}
            </div>
            </>
            )}

            {/* Hors du bloc conditionnel : on doit pouvoir enregistrer depuis
                l'aperçu, c'est là qu'on décide que l'article est prêt. */}
            <div className="blogs-form__actions">
              <span className="blogs-form__autosave" aria-live="polite">
                {autoSavedAt
                  ? `Sauvegardé sur cet appareil à ${formatHeure(autoSavedAt)}`
                  : dirty ? 'Modifications non enregistrées…' : ''}
              </span>
              <button type="button" className="blogs-btn" onClick={handleCancel} disabled={saving}>
                Annuler
              </button>
              <button
                type="button"
                className="blogs-btn"
                onClick={() => handleSubmit('brouillon')}
                disabled={saving}
              >
                {statutEdite === 'publie' && editingId ? 'Dépublier' : 'Enregistrer le brouillon'}
              </button>
              <button
                type="button"
                className="blogs-btn blogs-btn--primary"
                onClick={() => handleSubmit('publie')}
                disabled={saving}
              >
                {saving ? 'Enregistrement…'
                  : statutEdite === 'publie' && editingId ? 'Enregistrer les modifications'
                  : 'Publier'}
              </button>
            </div>
            {form.date_publication > aujourdHuiISO() && (
              <p className="blogs-field__hint blogs-form__schedule-hint">
                Publier avec cette date met l'article de côté jusqu'au {formatDate(form.date_publication)} :
                il n'apparaîtra pas dans le blog avant.
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default Blogs;
